import { FormattedDate, FormattedMessage } from 'react-intl';
import { Box, Flex } from '@theme-ui/components';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { rem } from '@app/styles/theme/layout';

import type { OrderItemFragment } from './__graphql__/OrderHistoryDetailContext';
import { useOrderHistoryDetail } from './OrderHistoryDetailContext';
import messages from './Summary.messages';
import { StatusLabel, SummaryContainer, SummaryItem } from './Summary.styles';

const Summary = () => {
  const { itemsData } = useOrderHistoryDetail();

  const trackingLinks = itemsData && getTrackingLinks(itemsData);

  return itemsData ? (
    <SummaryContainer>
      <SummaryItem sx={{ alignItems: 'center' }} py={3}>
        <Label sx={{ width: ['100%', '25%'] }}>
          <FormattedMessage {...messages.orderId} />
        </Label>
        <Box sx={{ width: ['100%', '75%'] }} data-testid="order-number">
          {itemsData.number}
        </Box>
      </SummaryItem>

      <SummaryItem sx={{ alignItems: 'center' }} py={3}>
        <Label sx={{ width: ['100%', '25%'] }}>
          <FormattedMessage {...messages.orderDate} />
        </Label>
        <Box sx={{ width: ['100%', '75%'] }} data-testid="order-date">
          <FormattedDate
            year="2-digit"
            month="2-digit"
            day="numeric"
            value={new Date(itemsData.orderDate)}
          />
        </Box>
      </SummaryItem>

      <SummaryItem sx={{ alignItems: 'center' }} py={3}>
        <Label sx={{ width: ['100%', '25%'] }}>
          <FormattedMessage {...messages.orderStatus} />
        </Label>
        <Box sx={{ width: ['100%', '75%'] }} data-testid="order-status">
          <StatusLabel>{itemsData.status}</StatusLabel>
        </Box>
      </SummaryItem>

      {Boolean(trackingLinks?.length) && (
        <SummaryItem sx={{ alignItems: 'center' }} py={3}>
          <Label sx={{ width: ['100%', '25%'] }}>
            <FormattedMessage {...messages.shipmentTracking} />
          </Label>
          <Flex
            sx={{
              width: ['100%', '75%'],
              flexDirection: 'column',
            }}
          >
            {trackingLinks?.map(
              (trackingLink, idx) =>
                trackingLink && (
                  <a
                    data-testid={`order-tracking-link-${idx}`}
                    key={idx}
                    href={trackingLink}
                    target="_blank"
                    rel="noreferrer"
                    css={css`
                      overflow-wrap: break-word;

                      &:not(:last-of-type) {
                        margin-bottom: ${rem(5)};
                      }
                    `}
                  >
                    <StatusLabel>{trackingLink}</StatusLabel>
                  </a>
                ),
            )}
          </Flex>
        </SummaryItem>
      )}
    </SummaryContainer>
  ) : null;
};

export default Summary;

const Label = styled(Box)`
  font-weight: 600;

  &::after {
    content: ':';
  }
`;

const getTrackingLinks = (
  itemsData: OrderItemFragment,
): (string | null | undefined)[] => {
  const trackingLinks = itemsData?.shipments?.flatMap((shipments) =>
    shipments?.tracking?.map((tracking) => tracking?.number),
  );

  return trackingLinks || [];
};
